import axios from 'axios';
import { ENV_CONFIG } from '../config/environment';

// Crear instancia de axios configurada
const api = axios.create({
  baseURL: ENV_CONFIG.API_BASE_URL,
  timeout: ENV_CONFIG.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    
    if (error.response?.status === 401) {
      // Token expirado o inválido
      console.log('Token inválido, redirigiendo a login...');
      localStorage.removeItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
      localStorage.removeItem(ENV_CONFIG.USER_KEY);
      localStorage.removeItem(ENV_CONFIG.COMPANY_KEY);
      window.location.href = '/';
    } else if (error.response?.status === 403) {
      // Sin permisos
      console.log('Sin permisos para esta acción');
    }
    return Promise.reject(error);
  }
);

class UserService {
  // Verificar si el usuario actual es super administrador
  isAdmin() {
    try {
      const user = JSON.parse(localStorage.getItem(ENV_CONFIG.USER_KEY) || '{}');
      return user.role === 'super_admin';
    } catch (error) {
      console.error('Error verificando permisos de super admin:', error);
      return false;
    }
  }

  // Verificar si el usuario actual es super admin
  isSuperAdmin() {
    try {
      const user = JSON.parse(localStorage.getItem(ENV_CONFIG.USER_KEY) || '{}');
      return user.role === 'super_admin';
    } catch (error) {
      console.error('Error verificando permisos de super admin:', error);
      return false;
    }
  }

  // Obtener usuario actual
  getCurrentUser() {
    try {
      return JSON.parse(localStorage.getItem(ENV_CONFIG.USER_KEY) || '{}');
    } catch (error) {
      console.error('Error obteniendo usuario actual:', error);
      return {};
    }
  }
  // Obtener todos los usuarios (Solo administradores)
  async getAllUsers() {
    try {
      console.log('🔍 Obteniendo lista de usuarios...');
      const response = await api.get('/users');
      console.log('📥 Respuesta completa de /users:', response.data);
      
      // La API puede devolver { status: 'success', data: [...] } o directamente [...]
      const usersData = response.data?.data || response.data || [];
      console.log('✅ Usuarios obtenidos:', usersData.length);
      return response.data; // Devolver toda la respuesta para que el componente maneje la estructura
    } catch (error) {
      console.error('❌ Error obteniendo usuarios:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('No tienes permisos para ver la lista de usuarios');
      }
      
      throw new Error(error.response?.data?.message || 'Error al obtener usuarios');
    }
  }

  // Obtener usuario por ID
  async getUserById(id) {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al obtener usuario');
    }
  }

  // Crear nuevo usuario (Solo administradores)
  async createUser(userData) {
    try {
      console.log('➕ Creando usuario:', userData.name);
      const response = await api.post('/users', {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role: userData.role
      });
      console.log('✅ Usuario creado exitosamente:', response.data?.user?.name);
      return response.data;
    } catch (error) {
      console.error('❌ Error creando usuario:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede crear usuarios');
      } else if (error.response?.status === 422) {
        const errors = error.response.data?.errors;
        if (errors?.email) {
          throw new Error('El email ya está en uso');
        }
        throw new Error('Datos de validación incorrectos');
      }
      
      throw new Error(error.response?.data?.message || 'Error al crear usuario');
    }
  }

  // Actualizar usuario (Administradores o propio usuario)
  async updateUser(id, userData) {
    try {
      console.log('✏️ Actualizando usuario ID:', id);
      const response = await api.put(`/users/${id}`, {
        name: userData.name,
        email: userData.email,
        role: userData.role
      });
      console.log('✅ Usuario actualizado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error actualizando usuario:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede actualizar usuarios');
      } else if (error.response?.status === 422) {
        const errors = error.response.data?.errors;
        if (errors?.email) {
          throw new Error('El email ya está en uso');
        }
        throw new Error('Datos de validación incorrectos');
      }
      
      throw new Error(error.response?.data?.message || 'Error al actualizar usuario');
    }
  }

  // Cambiar contraseña de usuario (Administradores o propio usuario)
  async changePassword(id, passwordData) {
    try {
      console.log('🔑 Cambiando contraseña para usuario ID:', id);
      const response = await api.put(`/users/${id}/password`, {
        password: passwordData.password
      });
      console.log('✅ Contraseña cambiada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error cambiando contraseña:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede cambiar contraseñas de otros usuarios');
      } else if (error.response?.status === 422) {
        throw new Error('La contraseña debe tener al menos 8 caracteres');
      }
      
      throw new Error(error.response?.data?.message || 'Error al cambiar contraseña');
    }
  }

  // Cambiar estado del usuario (Solo administradores)
  async updateUserStatus(id, status) {
    try {
      console.log(`🔄 Cambiando estado de usuario ID ${id} a:`, status);
      const response = await api.put(`/users/${id}/status`, {
        status: status
      });
      console.log('✅ Estado cambiado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error cambiando estado del usuario:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede cambiar el estado de usuarios');
      } else if (error.response?.data?.message?.includes('Super Administrador')) {
        throw new Error('No se puede cambiar el estado del Super Administrador');
      }
      
      throw new Error(error.response?.data?.message || 'Error al cambiar estado del usuario');
    }
  }

  // Eliminar usuario (Solo administradores)
  async deleteUser(id) {
    try {
      console.log('🗑️ Eliminando usuario ID:', id);
      const response = await api.delete(`/users/${id}`);
      console.log('✅ Usuario eliminado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error eliminando usuario:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        if (error.response?.data?.message?.includes('Super Administrador')) {
          throw new Error('No se puede eliminar al Super Administrador');
        }
        throw new Error('Solo el Super Administrador puede eliminar usuarios');
      }
      
      throw new Error(error.response?.data?.message || 'Error al eliminar usuario');
    }
  }

  // Obtener usuarios por rol
  async getUsersByRole(role) {
    try {
      const response = await api.get(`/users/role/${role}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo usuarios por rol:', error);
      throw new Error(error.response?.data?.message || 'Error al obtener usuarios por rol');
    }
  }

  // Obtener todos los roles disponibles
  async getAllRoles() {
    try {
      console.log('🔍 Obteniendo roles disponibles...');
      const response = await api.get('/users/roles');
      console.log('📥 Roles obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo roles:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener roles');
    }
  }

  // Obtener todos los permisos disponibles
  async getAllPermissions() {
    try {
      console.log('🔍 Obteniendo permisos disponibles...');
      const response = await api.get('/users/permissions');
      console.log('📥 Permisos obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo permisos:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Error al obtener permisos');
    }
  }

  // Asignar permiso a rol
  async assignPermissionToRole(roleId, permissionId) {
    try {
      console.log(`➕ Asignando permiso ${permissionId} al rol ${roleId}...`);
      const response = await api.post(`/users/roles/${roleId}/permissions`, {
        permission_id: permissionId
      });
      console.log('✅ Permiso asignado exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error asignando permiso:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede gestionar permisos de roles');
      } else if (error.response?.status === 400) {
        throw new Error('El rol ya tiene este permiso asignado');
      }
      
      throw new Error(error.response?.data?.message || 'Error al asignar permiso');
    }
  }

  // Quitar permiso de rol
  async removePermissionFromRole(roleId, permissionId) {
    try {
      console.log(`➖ Quitando permiso ${permissionId} del rol ${roleId}...`);
      const response = await api.delete(`/users/roles/${roleId}/permissions`, {
        data: { permission_id: permissionId }
      });
      console.log('✅ Permiso removido exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error removiendo permiso:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede gestionar permisos de roles');
      } else if (error.response?.status === 400) {
        throw new Error('El rol no tiene este permiso asignado');
      }
      
      throw new Error(error.response?.data?.message || 'Error al remover permiso');
    }
  }

  // Obtener estadísticas de usuarios (Solo administradores)
  async getUserStats() {
    try {
      console.log('📊 Obteniendo estadísticas de usuarios...');
      const response = await api.get('/users/stats');
      console.log('✅ Estadísticas obtenidas:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas:', error.response?.data || error.message);
      
      if (error.response?.status === 403) {
        throw new Error('Solo el Super Administrador puede ver estadísticas');
      }
      
      throw new Error(error.response?.data?.message || 'Error al obtener estadísticas');
    }
  }

  // Validar email único
  async validateEmail(email, excludeId = null) {
    try {
      const response = await api.post('/users/validate-email', {
        email: email,
        excludeId: excludeId
      });
      return response.data;
    } catch (error) {
      console.error('Error validando email:', error);
      throw new Error(error.response?.data?.message || 'Error al validar email');
    }
  }

  // Resetear contraseña (Ruta pública)
  async resetPassword(email) {
    try {
      console.log('🔄 Reseteando contraseña para:', email);
      const response = await api.post('/users/reset-password', {
        email: email
      });
      console.log('✅ Contraseña reseteada exitosamente');
      return response.data;
    } catch (error) {
      console.error('❌ Error reseteando contraseña:', error.response?.data || error.message);
      
      if (error.response?.status === 422) {
        throw new Error('Email no encontrado en el sistema');
      }
      
      throw new Error(error.response?.data?.message || 'Error al resetear contraseña');
    }
  }

  // Verificar conexión con la API
  async checkApiConnection() {
    try {
      const response = await api.get('/user'); // Ruta de test
      return { connected: true, user: response.data?.user };
    } catch (error) {
      console.warn('⚠️ API no disponible, usando modo offline');
      return { connected: false, error: error.message };
    }
  }
}

export default new UserService(); 